import React from "react";
import { Close } from "@material-ui/icons";
import { Modal, Fade, Backdrop } from "@material-ui/core";
import { useTranslation } from "react-i18next";

import { showAuthError, returnCurrentLink } from "../utils/validations";
import useStyles from "./styles/authDialogStyles";
import { Store } from "../hooks/main_store";
import AuthService from "../services/auth";
import { authService } from "../services/firebase";
import * as Routes from "../utils/routes";
import Model from "../hooks/Model";

const AuthDialog = ({ open = false, onClose = () => {} }) => {
  const { t } = useTranslation();
  const styles = useStyles();
  const { state } = React.useContext(Store);

  const [vc, setVc] = React.useState({
    showPassword: false,
    email: "",
    password: "",
    showRegister: false,
    showPrompt: false,
    showOTP: false,
    otpCode: "",
  });
  const changeVc = (data) => setVc((ov) => ({ ...ov, ...data }));

  const changeInput = (name) => (event) => {
    event.persist();
    setVc((oldValues) => ({
      ...oldValues,
      [name]: event.target.value,
    }));
  };

  const loginWithLink = async () => {
    try {
      Model.setData("opacity", 0.7);
      Model.setData("loading", true);
      if (!vc.email) {
        throw new Error(t("components.authDialog.mustFillAllFields"));
      } else {
        const emailLower = vc.email.toLowerCase().replace(/ /g, "");
        const response = await AuthService.sendAuthOTPEmail({
          email: emailLower,
          url: window.location.origin,
          domain: window.location.host,
        });

        if (response.ok) {
          changeVc({ showOTP: true });
        } else {
          throw response.data;
        }
      }
    } catch (e) {
      Model.updateAlerts({
        message: showAuthError(e?.message || e),
        variant: "error",
      });
    } finally {
      Model.setData("loading", false);
    }
  };

  const verifyOTP = async () => {
    try {
      Model.setData("opacity", 0.7);
      Model.setData("loading", true);
      if (!vc.otpCode || vc.otpCode.length !== 4) {
        throw new Error(t("components.authDialog.mustFillAllFields"));
      } else {
        const emailLower = vc.email.toLowerCase().replace(/ /g, "");
        const response = await AuthService.getAuthToken({
          email: emailLower,
          code: vc.otpCode,
        });

        if (response.ok && response.data?.token) {
          const authResult = await authService.signInWithCustomToken(
            response.data.token,
          );
          const uid = authResult.user?.uid || authService.currentUser()?.uid;
          if (uid) {
            await AuthService.getMe(uid);
            Model.setData("uid", uid);
          }
          onClose();
        } else {
          throw response.data;
        }
      }
    } catch (e) {
      Model.updateAlerts({
        message: showAuthError(e?.message || e),
        variant: "error",
      });
    } finally {
      Model.setData("loading", false);
    }
  };

  const onKeyDown = (event) => {
    if (event.keyCode === 13) {
      event.preventDefault();
      loginWithLink();
    }
  };

  React.useEffect(() => {
    setVc({
      showPassword: false,
      email: "",
      password: "",
      showRegister: false,
      showOTP: false,
      otpCode: "",
    });
  }, [open]);

  React.useEffect(() => {
    if (state.uid) {
      onClose();
    }
    //eslint-disable-next-line
  }, [state.uid]);

  React.useEffect(() => {
    const unsubscribe = authService.onAuthStateChanged((user) => {
      // Nothing for OTP flow
    });

    return () => unsubscribe();
    //eslint-disable-next-line
  }, []);

  if (state.loading) {
    return <></>;
  }

  return (
    <>
      <Modal
        className={styles.contModal}
        open={open}
        onClose={onClose}
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{ timeout: 500 }}
      >
        <Fade in={open}>
          <div className={styles.contForm}>
            <div className={styles.rowTitle}>
              <p className={styles.title}>{t("components.authDialog.logIn")}</p>
              <button className={styles.closeBtn} onClick={onClose}>
                <Close />
              </button>
            </div>
            <div className={styles.contInputs}>
              {vc.showOTP ? (
                <>
                  <p
                    className={styles.termsLbl}
                    style={{
                      textAlign: "center",
                      marginBottom: 15,
                      fontSize: 18,
                      marginTop: 0,
                    }}
                  >
                    Introduce el código de 4 dígitos que te enviamos por correo
                    electrónico.
                  </p>
                  <input
                    className={styles.input}
                    placeholder="Código de verificación"
                    type="text"
                    onChange={changeInput("otpCode")}
                    value={vc.otpCode}
                    maxLength={4}
                    autoFocus
                    onKeyDown={(e) => {
                      if (e.keyCode === 13) {
                        e.preventDefault();
                        verifyOTP();
                      }
                    }}
                    style={{
                      textAlign: "center",
                      letterSpacing: 5,
                      fontSize: 18,
                      marginBottom: 12,
                    }}
                  />
                  <button className={styles.fullBtn} onClick={verifyOTP}>
                    {t("commons.accept") || "Verify"}
                  </button>
                  <button
                    className={styles.fullBtn}
                    onClick={() => changeVc({ showOTP: false, otpCode: "" })}
                    style={{
                      marginTop: 10,
                      backgroundColor: "transparent",
                      color: "gray",
                      border: "1px solid gray",
                    }}
                  >
                    {t("commons.cancel") || "Cancel"}
                  </button>
                </>
              ) : (
                <>
                  <input
                    className={styles.input}
                    placeholder={t("commons.email")}
                    type="email"
                    onChange={changeInput("email")}
                    value={vc.email}
                    maxLength={320}
                    autoFocus
                    onKeyDown={onKeyDown}
                  />
                  <div className={styles.termsLbl}>
                    {t("components.authDialog.onClickAccept")}{" "}
                    <a
                      href={returnCurrentLink(
                        Routes.RENDER_LEGAL_DOC + "?document=terms",
                      )}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {t("components.authDialog.useConditions")}
                    </a>
                    , {t("components.authDialog.the")}{" "}
                    <a
                      href={returnCurrentLink(
                        Routes.RENDER_LEGAL_DOC + "?document=privacy",
                      )}
                      rel="noopener noreferrer"
                      target="_blank"
                    >
                      {t("components.authDialog.privacyPolicy")}
                    </a>
                    , {t("components.authDialog.receiveNotifications")}
                  </div>
                  <button className={styles.fullBtn} onClick={loginWithLink}>
                    {vc.showRegister
                      ? t("components.authDialog.createAccount")
                      : t("components.authDialog.enterAccount")}
                  </button>
                </>
              )}
            </div>
          </div>
        </Fade>
      </Modal>
    </>
  );
};

export default AuthDialog;
